const chapter = {
  id: 'hyperbolic-functions',
  slug: 'hyperbolic-functions',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Advanced',
  title: {
    english: 'Hyperbolic Functions',
        hindi: 'अतिपरवलयिक फलन',
  },
  estReadMinutes: 35,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: 'Occasionally tested in JEE Advanced; useful for integrals',
        hindi: 'कभी-कभी जेईई एडवांस में परीक्षण किया जाता है; इंटीग्रल के लिए उपयोगी',
  },
  syllabusTags: ['Calculus', 'JEE Advanced'],
  sections: [
    {
      id: 'definitions',
      title: {
        english: 'Definitions',
        hindi: 'परिभाषाएँ',
      },
      blocks: [
        {
          type: 'paragraph',
          body: {
            english: 'Hyperbolic functions are defined in terms of $e^x$. They satisfy identities analogous to (but slightly different from) circular trigonometric functions, and parameterise the hyperbola $x^2 - y^2 = 1$ just as $(\\cos\\theta, \\sin\\theta)$ parameterises the unit circle.',
        hindi: 'हाइपरबोलिक फ़ंक्शंस को $e^x$ के संदर्भ में परिभाषित किया गया है। वे वृत्ताकार त्रिकोणमितीय कार्यों के अनुरूप (लेकिन थोड़ा अलग) पहचानों को संतुष्ट करते हैं, और हाइपरबोला $x^2 - y^2 = 1$ को पैरामीटराइज़ करते हैं जैसे $(\\cos\\theta, \\sin\\theta)$ इकाई वृत्त को पैरामीटराइज़ करते हैं।',
          },
        },
        {
          type: 'equation',
          equation: '\\sinh x = \\dfrac{e^x - e^{-x}}{2}, \\quad \\cosh x = \\dfrac{e^x + e^{-x}}{2}',
        },
        {
          type: 'equation',
          equation: '\\tanh x = \\dfrac{\\sinh x}{\\cosh x} = \\dfrac{e^x - e^{-x}}{e^x + e^{-x}}',
        },
        {
          type: 'equation',
          equation: '\\coth x = \\dfrac{1}{\\tanh x},\\ \\ \\text{sech}\\,x = \\dfrac{1}{\\cosh x},\\ \\ \\text{csch}\\,x = \\dfrac{1}{\\sinh x}',
        },
      ],
    },
    {
      id: 'domains',
      title: {
        english: 'Domains, Ranges & Parity',
        hindi: 'प्रान्त, परिसर और सम-विषमता',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Function', 'Domain', 'Range', 'Parity'],
          rows: [
            ['$\\sinh x$', '$\\mathbb{R}$', '$\\mathbb{R}$', 'Odd'],
            ['$\\cosh x$', '$\\mathbb{R}$', '$[1, \\infty)$', 'Even'],
            ['$\\tanh x$', '$\\mathbb{R}$', '$(-1, 1)$', 'Odd'],
            ['$\\coth x$', '$\\mathbb{R} \\setminus \\{0\\}$', '$\\mathbb{R} \\setminus [-1, 1]$', 'Odd'],
            ['$\\text{sech}\\,x$', '$\\mathbb{R}$', '$(0, 1]$', 'Even'],
            ['$\\text{csch}\\,x$', '$\\mathbb{R} \\setminus \\{0\\}$', '$\\mathbb{R} \\setminus \\{0\\}$', 'Odd'],
          ],
        },
      ],
    },
    {
      id: 'identities',
      title: {
        english: 'Key Identities (Osborn\'s Rule)',
        hindi: 'प्रमुख पहचान (ओस्बोर्न का नियम)',
      },
      blocks: [
        {
          type: 'tip',
          body: {
            english: '**Osborn\'s rule**: take any trigonometric identity; replace cos→cosh, sin→sinh; **flip the sign of any product (or implied product) of two sinh terms**.',
        hindi: '**ओस्बोर्न का नियम**: किसी भी त्रिकोणमितीय सर्वसमिका में $\\cos \\to \\cosh$, $\\sin \\to \\sinh$ रखें; और **दो $\\sinh$ पदों के गुणनफल (या निहित गुणनफल) का चिह्न बदल दें**।',
          },
        },
        {
          type: 'equation',
          equation: '\\cosh^2 x - \\sinh^2 x = 1',
        },
        {
          type: 'equation',
          equation: '1 - \\tanh^2 x = \\text{sech}^2 x, \\quad \\coth^2 x - 1 = \\text{csch}^2 x',
        },
        {
          type: 'equation',
          equation: '\\sinh(x \\pm y) = \\sinh x \\cosh y \\pm \\cosh x \\sinh y',
        },
        {
          type: 'equation',
          equation: '\\cosh(x \\pm y) = \\cosh x \\cosh y \\pm \\sinh x \\sinh y',
        },
        {
          type: 'equation',
          equation: '\\sinh 2x = 2 \\sinh x \\cosh x, \\quad \\cosh 2x = \\cosh^2 x + \\sinh^2 x = 1 + 2\\sinh^2 x',
        },
        {
          type: 'note',
          body: {
            english: 'Compare with circular: $\\sin(x+y) = \\sin x \\cos y + \\cos x \\sin y$ (same form). But $\\cos(x+y) = \\cos x \\cos y - \\sin x \\sin y$ has a **minus**, while $\\cosh(x+y)$ has a **plus** — the sign flipped because of the sinh·sinh product.',
        hindi: 'परिपत्र के साथ तुलना करें: $\\sin(x+y) = \\sin x \\cos y + \\cos x \\sin y$ (समान रूप)। लेकिन $\\cos(x+y) = \\cos x \\cos y - \\sin x \\sin y$ में **माइनस** है, जबकि $\\cosh(x+y)$ में **प्लस** है - सिंह·सिंह उत्पाद के कारण चिन्ह पलट गया।',
          },
        },
      ],
    },
    {
      id: 'derivatives-integrals',
      title: {
        english: 'Derivatives & Integrals',
        hindi: 'अवकलज और समाकल',
      },
      blocks: [
        {
          type: 'equation',
          equation: '\\dfrac{d}{dx}\\sinh x = \\cosh x, \\quad \\dfrac{d}{dx}\\cosh x = \\sinh x',
        },
        {
          type: 'equation',
          equation: '\\dfrac{d}{dx}\\tanh x = \\text{sech}^2 x, \\quad \\dfrac{d}{dx}\\coth x = -\\text{csch}^2 x',
        },
        {
          type: 'equation',
          equation: '\\dfrac{d}{dx}\\text{sech}\\,x = -\\text{sech}\\,x \\tanh x',
        },
        {
          type: 'note',
          body: {
            english: '$\\dfrac{d}{dx}\\cosh x = +\\sinh x$ — **no minus sign** (unlike $\\cos$). This is the crucial difference.',
        hindi: '$\\dfrac{d}{dx}\\cosh x = +\\sinh x$ - **कोई ऋण चिह्न नहीं** ($\\cos$ के विपरीत)। यह महत्वपूर्ण अंतर है.',
          },
        },
        {
          type: 'equation',
          equation: '\\int \\sinh x\\,dx = \\cosh x + C, \\quad \\int \\cosh x\\,dx = \\sinh x + C',
        },
      ],
    },
    {
      id: 'inverse',
      title: {
        english: 'Inverse Hyperbolic Functions (Logarithmic Forms)',
        hindi: 'प्रतिलोम अतिपरवलयिक फलन (लघुगणक रूप)',
      },
      blocks: [
        {
          type: 'equation',
          equation: '\\sinh^{-1} x = \\ln\\big(x + \\sqrt{x^2 + 1}\\big), \\quad x \\in \\mathbb{R}',
        },
        {
          type: 'equation',
          equation: '\\cosh^{-1} x = \\ln\\big(x + \\sqrt{x^2 - 1}\\big), \\quad x \\ge 1',
        },
        {
          type: 'equation',
          equation: '\\tanh^{-1} x = \\dfrac{1}{2} \\ln \\dfrac{1+x}{1-x}, \\quad |x| < 1',
        },
        {
          type: 'equation',
          equation: '\\dfrac{d}{dx}\\sinh^{-1} x = \\dfrac{1}{\\sqrt{x^2 + 1}}, \\quad \\dfrac{d}{dx}\\cosh^{-1} x = \\dfrac{1}{\\sqrt{x^2 - 1}}',
        },
        {
          type: 'equation',
          equation: '\\dfrac{d}{dx}\\tanh^{-1} x = \\dfrac{1}{1 - x^2}',
        },
        {
          type: 'tip',
          body: {
            english: 'Useful integrals: $\\displaystyle\\int \\dfrac{dx}{\\sqrt{x^2 + a^2}} = \\sinh^{-1}(x/a) + C$; $\\displaystyle\\int \\dfrac{dx}{\\sqrt{x^2 - a^2}} = \\cosh^{-1}(x/a) + C$.',
        hindi: 'उपयोगी इंटीग्रल: $\\displaystyle\\int \\dfrac{dx}{\\sqrt{x^2 + a^2}} = \\sinh^{-1}(x/a) + C$; $\\displaystyle\\int \\dfrac{dx}{\\sqrt{x^2 - a^2}} = \\cosh^{-1}(x/a) + C$.',
          },
        },
      ],
    },
    {
      id: 'examples',
      title: {
        english: 'Worked Examples',
        hindi: 'हल किये गये उदाहरण',
      },
      blocks: [
        {
          type: 'example',
          problem: {
            english: 'If $\\sinh x = 3/4$, find $\\cosh x$ and $\\tanh x$.',
        hindi: 'यदि $\\sinh x = 3/4$, तो $\\cosh x$ और $\\tanh x$ खोजें।',
          },
          solution: {
            english: '$\\cosh^2 x = 1 + 9/16 = 25/16 \\Rightarrow \\cosh x = 5/4$ (always positive). $\\tanh x = (3/4) / (5/4) = 3/5$.',
        hindi: '$\\cosh^2 x = 1 + 9/16 = 25/16 \\Rightarrow \\cosh x = 5/4$ (हमेशा सकारात्मक)। $\\tanh x = (3/4) / (5/4) = 3/5$।',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'Evaluate $\\displaystyle\\int_0^1 \\dfrac{dx}{\\sqrt{x^2 + 1}}$.',
        hindi: '$\\displaystyle\\int_0^1 \\dfrac{dx}{\\sqrt{x^2 + 1}}$ का मूल्यांकन करें.',
          },
          solution: {
            english: '$= \\big[\\sinh^{-1} x\\big]_0^1 = \\ln(1 + \\sqrt 2) - \\ln 1 = \\ln(1 + \\sqrt 2)$.',
        hindi: '$= \\Big[\\sinh^{-1} x\\Big]_0^1 = \\ln(1 + \\sqrt 2) - \\ln 1 = \\ln(1 + \\sqrt 2)$.',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'Show $\\sinh 2x = \\dfrac{2 \\tanh x}{1 - \\tanh^2 x}$.',
        hindi: '$\\sinh 2x = \\dfrac{2 \\tanh x}{1 - \\tanh^2 x}$ दिखाएँ.',
          },
          solution: {
            english: '$\\dfrac{2\\tanh x}{1 - \\tanh^2 x} = \\dfrac{2 \\sinh x / \\cosh x}{\\text{sech}^2 x} = \\dfrac{2 \\sinh x \\cosh x}{1} = \\sinh 2x.$',
        hindi: '$\\dfrac{2\\tanh x}{1 - \\tanh^2 x} = \\dfrac{2 \\sinh x / \\cosh x}{\\text{sech}^2 x} = \\dfrac{2 \\sinh x \\cosh x}{1} = \\sinh 2x.$',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\sinh x = \\dfrac{e^x - e^{-x}}{2},\\ \\cosh x = \\dfrac{e^x + e^{-x}}{2}',
      desc: {
        english: '',
        hindi: 'एक्स = 2 ,\\ एक्स = 2',
      },
    },
    {
      latex: '\\cosh^2 x - \\sinh^2 x = 1',
      desc: {
        english: 'Pythagorean',
        hindi: 'पायथागॉरियन',
      },
    },
    {
      latex: '\\cosh(x+y) = \\cosh x \\cosh y + \\sinh x \\sinh y',
      desc: {
        english: 'Note: + sign',
        hindi: 'नोट: + चिन्ह',
      },
    },
    {
      latex: '\\dfrac{d}{dx}\\cosh x = +\\sinh x',
      desc: {
        english: 'No minus',
        hindi: 'कोई माइनस नहीं',
      },
    },
    {
      latex: '\\sinh^{-1} x = \\ln(x + \\sqrt{x^2+1})',
      desc: {
        english: '',
        hindi: '-1 एक्स = (एक्स + )',
      },
    },
    {
      latex: '\\tanh^{-1} x = \\tfrac12 \\ln\\dfrac{1+x}{1-x}',
      desc: {
        english: '',
        hindi: '-1 एक्स = 12 1-एक्स',
      },
    },
    {
      latex: '\\int \\dfrac{dx}{\\sqrt{x^2 + a^2}} = \\sinh^{-1}(x/a) + C',
      desc: {
        english: '',
        hindi: '= -1 (x/a) + C',
      },
    },
  ],
  pyqs: [
    {
      year: 2023,
      exam: 'JEE Adv',
      tag: 'Identity',
      problem: {
        english: 'If $\\cosh x + \\sinh x = e^x$, find $(\\cosh x + \\sinh x)^n$.',
        hindi: 'यदि $\\cosh x + \\sinh x = e^x$, तो $(\\cosh x + \\sinh x)^n$ खोजें।',
      },
      answer: {
        english: '$e^{nx} = \\cosh nx + \\sinh nx$',
        hindi: '$e^{nx} = \\cosh nx + \\sain nx$',
      },
      solution: {
        english: 'Hyperbolic analog of De Moivre.',
        hindi: 'डी मोइवर का हाइपरबोलिक एनालॉग।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Adv',
      tag: 'Inverse',
      problem: {
        english: 'Evaluate $\\sinh^{-1}(\\sqrt 3)$.',
        hindi: '$\\sinh^{-1}(\\sqrt 3)$ का मूल्यांकन करें।',
      },
      answer: {
        english: '$\\ln(\\sqrt 3 + 2)$',
        hindi: '$\\ln(\\sqrt 3 + 2)$',
      },
      solution: {
        english: '$\\sinh^{-1} x = \\ln(x + \\sqrt{x^2+1})$ with $x = \\sqrt 3$.',
        hindi: '$\\sinh^{-1} x = \\ln(x + \\sqrt{x^2+1})$ $x = \\sqrt 3$ के साथ।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Adv',
      tag: 'Integral',
      problem: {
        english: 'Compute $\\displaystyle\\int \\dfrac{dx}{\\sqrt{4 + x^2}}$.',
        hindi: '$\\displaystyle\\int \\dfrac{dx}{\\sqrt{4 + x^2}}$ की गणना करें.',
      },
      answer: {
        english: '$\\sinh^{-1}(x/2) + C$',
        hindi: '$\\sinh^{-1}(x/2) + C$',
      },
      solution: {
        english: 'Standard form with $a = 2$.',
        hindi: '$a = 2$ के साथ मानक फॉर्म।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Adv',
      tag: 'Derivative',
      problem: {
        english: '$\\dfrac{d}{dx}[\\tanh^{-1}(\\sin x)] = ?$',
        hindi: '$\\dfrac{d}{dx}[\\tanh^{-1}(\\sin x)] =?$',
      },
      answer: {
        english: '$\\sec x$',
        hindi: '$\\sec x$',
      },
      solution: {
        english: '$\\dfrac{\\cos x}{1 - \\sin^2 x} = \\dfrac{\\cos x}{\\cos^2 x} = \\sec x$.',
        hindi: '$\\dfrac{\\cos x}{1 - \\sin^2 x} = \\dfrac{\\cos x}{\\cos^2 x} = \\sec x$.',
      },
    },
    {
      year: 2018,
      exam: 'JEE Adv',
      tag: 'Identity',
      problem: {
        english: 'Prove $\\cosh 2x = 2 \\cosh^2 x - 1$.',
        hindi: '$\\cosh 2x = 2 \\cosh^2 x - 1$ सिद्ध करें।',
      },
      answer: {
        english: 'From $\\cosh 2x = \\cosh^2 x + \\sinh^2 x = \\cosh^2 x + (\\cosh^2 x - 1) = 2\\cosh^2 x - 1$.',
        hindi: '$\\cosh 2x = \\cosh^2 x + \\sinh^2 x = \\cosh^2 x + (\\cosh^2 x - 1) = 2\\cosh^2 x - 1$ से.',
      },
      solution: {
        english: '',
        hindi: '$\\cosh 2x = \\cosh^2x + \\sinh^2x$ तथा $\\sinh^2x = \\cosh^2x - 1$ रखने पर $\\cosh 2x = 2\\cosh^2x - 1$ मिलता है।',
      },
    },
  ],
  next: {
    slug: 'solution-of-triangles',
    title: 'Solution of Triangles & Heights and Distances',
  },
};

export default chapter;
